import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ExternalLink } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageState } from '@/components/layout/PageState';
import { eventService } from '@/lib/api/event.service';
import type { Event } from '@/types/event.types';

interface ManagedEvent extends Event {
  celebration_title: string;
  user_role: string;
}

export const ManagedEvents: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const [events, setEvents] = useState<ManagedEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<ManagedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadManagedEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, searchQuery]);

  const loadManagedEvents = async () => {
    try {
      setLoading(true);
      const data = await eventService.getManagedEvents();
      setEvents(data as ManagedEvent[]);
      setError(null);
    } catch (err) {
      setError(t('events:errors.loadFailed') || 'Failed to load managed events');
      console.error('Error loading managed events:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        event =>
          event.title.toLowerCase().includes(query) ||
          event.celebration_title.toLowerCase().includes(query)
      );
    }

    setFilteredEvents(filtered);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'organizer':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'event_manager':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'organizer':
        return t('common:team.roles.organizer') || 'Organizer';
      case 'event_manager':
        return t('common:team.roles.event_manager') || 'Event Manager';
      default:
        return role;
    }
  };

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <PageContainer width="default" className="space-y-6">
        <AppBreadcrumbs />
        <PageState kind="loading" title={t('common:loading')} />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer width="default" className="space-y-6">
        <AppBreadcrumbs />
        <PageState 
          kind="error" 
          title={t('common:errors.loadFailed') || 'Failed to load events'}
          description={error}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="default" className="space-y-6">
      <AppBreadcrumbs />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('events:managedEvents.title') || 'Events I Manage'}
        </h1>
        <p className="text-sm text-gray-500">
          {t('events:managedEvents.subtitle') ||
            'View all events you manage across your celebrations'}
        </p>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <Input
            placeholder={t('common:search') || 'Search events...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full md:w-64"
          />
        </CardContent>
      </Card>

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('events:managedEvents.empty.title') || 'No managed events yet'}
            </h3>
            <p className="text-sm text-gray-500">
              {t('events:managedEvents.empty.description') ||
                'You are not managing any events yet. Ask an organizer to invite you as an Event Manager.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredEvents.map((event) => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              className="block"
            >
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Event Title & Role */}
                      <div className="flex items-start gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {event.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {event.celebration_title}
                          </p>
                        </div>
                        <Badge className={`whitespace-nowrap ${getRoleBadgeColor(event.user_role)}`}>
                          {getRoleLabel(event.user_role)}
                        </Badge>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm text-gray-600">
                        {/* Date */}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span>{formatEventDate(event.event_date)}</span>
                        </div>

                        {/* Venue */}
                        {event.venue_name && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{event.venue_name}</span>
                          </div>
                        )}

                        {/* Max Guests */}
                        {event.max_guests && (
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span>{event.max_guests} guests</span>
                          </div>
                        )}
                      </div>

                      {/* Status Badge */}
                      {event.status && (
                        <div className="mt-3 flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className="text-xs"
                          >
                            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Open Icon */}
                    <ExternalLink className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Results Count */}
      {filteredEvents.length > 0 && (
        <div className="text-sm text-gray-500 text-center">
          {t('common:showing')} {filteredEvents.length} {t('common:events')}
        </div>
      )}
    </PageContainer>
  );
};
